import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

export interface UserInfo {
  id: number;
  email: string;
  name: string;
  role: string;
  role_label: string;
  avatar_url: string | null;
  allowed_modules: string[];
  permissions: { resource: string; action: string }[];
}

interface UserContextValue {
  user: UserInfo | null;
  loading: boolean;
  refetch: () => void;
}

const UserContext = createContext<UserContextValue>({
  user: null,
  loading: true,
  refetch: () => {},
});

export function UserProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserInfo | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchUser = async () => {
    try {
      const res = await fetch("/api/rbac/me", { credentials: "include" });
      if (res.ok) {
        setUser(await res.json());
      }
    } catch {
      // silently fail — user will see full sidebar
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUser(); }, []);

  return (
    <UserContext.Provider value={{ user, loading, refetch: fetchUser }}>
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  return useContext(UserContext);
}
